import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, AlertCircle, Check, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { db } from '@/lib/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, getDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import type { ApplicationSource, ApplicationSourceFormData } from '@/features/application-sources/types';
import type { PipelineStatus } from '@/features/merchant-application/types';

const PIPELINE_STATUSES: PipelineStatus[] = [
  'lead',
  'phone',
  'offer',
  'underwriting',
  'documents',
  'approved',
];

interface BankingPartner {
  id: string;
  name: string;
  isActive: boolean;
}

export function ApplicationSourcesManagement() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sources, setSources] = useState<ApplicationSource[]>([]);
  const [bankingPartners, setBankingPartners] = useState<BankingPartner[]>([]);
  const [defaultSourceId, setDefaultSourceId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<ApplicationSource | null>(null);
  const [deletingSource, setDeletingSource] = useState<ApplicationSource | null>(null);
  const [formData, setFormData] = useState<ApplicationSourceFormData>({
    name: '',
    displayName: '',
    description: '',
    color: '#3B82F6',
    defaultPipelineStatus: 'lead',
    bankingPartnerId: undefined,
    isActive: true,
  });

  const loadSources = async () => {
    try {
      setLoading(true);
      const sourcesQuery = query(
        collection(db, 'applicationSources'),
        orderBy('order', 'asc')
      );
      const snapshot = await getDocs(sourcesQuery);
      const loadedSources = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as ApplicationSource[];

      setSources(loadedSources);

      // Load default source from global merchant settings
      const settingsRef = doc(db, 'merchantApplicationSettings', 'config');
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setDefaultSourceId(settings.defaultSource || null);
      }
    } catch (error) {
      console.error('Error loading application sources:', error);
      toast({
        title: 'Error',
        description: 'Failed to load application sources',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBankingPartners = async () => {
    try {
      const partnersQuery = query(
        collection(db, 'bankingPartners'),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(partnersQuery);
      const loadedPartners = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || '',
        isActive: doc.data().isActive !== false,
      })) as BankingPartner[];

      setBankingPartners(loadedPartners);
    } catch (error) {
      console.error('Error loading banking partners:', error);
    }
  };

  const handleSetDefaultSource = async (sourceId: string) => {
    try {
      setSaving(true);

      // Update GLOBAL merchant settings with default source
      const settingsRef = doc(db, 'merchantApplicationSettings', 'config');
      await updateDoc(settingsRef, {
        defaultSource: sourceId,
        updatedAt: Timestamp.now(),
      });

      setDefaultSourceId(sourceId);

      toast({
        title: 'Success',
        description: 'Default source updated successfully',
      });
    } catch (error) {
      console.error('Error setting default source:', error);
      toast({
        title: 'Error',
        description: 'Failed to set default source',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadSources();
    loadBankingPartners();
  }, []);

  const handleOpenDialog = (source?: ApplicationSource) => {
    if (source) {
      setEditingSource(source);
      setFormData({
        name: source.name,
        displayName: source.displayName,
        description: source.description || '',
        color: source.color || '#3B82F6',
        defaultPipelineStatus: source.defaultPipelineStatus,
        bankingPartnerId: source.bankingPartnerId,
        isActive: source.isActive,
      });
    } else {
      setEditingSource(null);
      setFormData({
        name: '',
        displayName: '',
        description: '',
        color: '#3B82F6',
        defaultPipelineStatus: 'lead',
        bankingPartnerId: undefined,
        isActive: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingSource(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.displayName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Name and Display Name are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      const now = Timestamp.now();

      if (editingSource) {
        // Update existing source
        await updateDoc(doc(db, 'applicationSources', editingSource.id), {
          ...formData,
          updatedAt: now,
        });

        // Also update merchant settings with banking partner for this source
        const settingsRef = doc(db, 'merchantApplicationSettings', 'config');
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          const sources = settings.sources || {};
          // Update the source's banking partner in merchant settings
          sources[editingSource.id] = {
            ...sources[editingSource.id],
            bankingPartnerId: formData.bankingPartnerId || null,
          };

          await updateDoc(settingsRef, {
            sources,
            updatedAt: now,
          });
        }

        toast({
          title: 'Success',
          description: 'Application source updated successfully',
        });
      } else {
        // Create new source
        const maxOrder = sources.reduce((max, s) => Math.max(max, s.order || 0), 0);
        const newSourceRef = await addDoc(collection(db, 'applicationSources'), {
          ...formData,
          order: maxOrder + 1,
          isDefault: false,
          createdAt: now,
          updatedAt: now,
        });

        // Also add to merchant settings
        const settingsRef = doc(db, 'merchantApplicationSettings', 'config');
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          const sources = settings.sources || {};

          // Add the new source's banking partner in merchant settings
          sources[newSourceRef.id] = {
            ...sources[newSourceRef.id],
            bankingPartnerId: formData.bankingPartnerId || null,
          };

          await updateDoc(settingsRef, {
            sources,
            updatedAt: now,
          });
        }

        toast({
          title: 'Success',
          description: 'Application source created successfully',
        });
      }

      await loadSources();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving application source:', error);
      toast({
        title: 'Error',
        description: 'Failed to save application source',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingSource) return;

    if (deletingSource.isDefault) {
      toast({
        title: 'Cannot Delete',
        description: 'Default sources cannot be deleted',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);

      // Delete from applicationSources collection
      await deleteDoc(doc(db, 'applicationSources', deletingSource.id));

      // Also remove from merchant settings
      const settingsRef = doc(db, 'merchantApplicationSettings', 'config');
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        const sources = settings.sources || {};

        // Remove the source from merchant settings
        delete sources[deletingSource.name];

        await updateDoc(settingsRef, {
          sources,
          updatedAt: Timestamp.now(),
        });
      }

      toast({
        title: 'Success',
        description: 'Application source deleted successfully',
      });
      await loadSources();
      setIsDeleteDialogOpen(false);
      setDeletingSource(null);
    } catch (error) {
      console.error('Error deleting application source:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete application source',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600">Loading application sources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Application Sources</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage application sources and configure settings for each source
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} disabled={saving}>
          <Plus className="h-4 w-4 mr-2" />
          New Source
        </Button>
      </div>

      {/* Info Banner */}
      {sources.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-blue-900">No application sources yet</h3>
              <p className="text-sm text-blue-700 mt-1">
                Create your first application source to start tracking merchant applications.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sources Table */}
      {sources.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold w-16">Default</TableHead>
                <TableHead className="font-semibold">Source Name</TableHead>
                <TableHead className="font-semibold">Display Name</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Default Pipeline</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sources.map((source) => (
                <TableRow key={source.id} className="hover:bg-gray-50">
                  <TableCell>
                    <button
                      onClick={() => handleSetDefaultSource(source.id)}
                      disabled={saving}
                      className="flex items-center justify-center w-full"
                    >
                      {defaultSourceId === source.id ? (
                        <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      ) : (
                        <Star className="h-5 w-5 text-gray-300 hover:text-gray-400" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div
                        className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                        style={{ backgroundColor: source.color || '#3B82F6' }}
                      >
                        {source.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{source.name}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-900">{source.displayName}</span>
                  </TableCell>
                  <TableCell>
                    {source.isActive ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
                        <Check className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-700 border-gray-200">
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {source.defaultPipelineStatus ? (
                      <Badge variant="outline" className="text-xs capitalize">
                        {source.defaultPipelineStatus.replace('-', ' ')}
                      </Badge>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="max-w-xs truncate text-sm text-gray-600">
                      {source.description || <span className="text-gray-400">—</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(source)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDeletingSource(source);
                          setIsDeleteDialogOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={source.isDefault}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingSource ? 'Edit Application Source' : 'New Application Source'}
            </DialogTitle>
            <DialogDescription>
              {editingSource
                ? 'Update application source information'
                : 'Add a new application source to track merchant applications'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Source Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="InvoiceBug, Direct, etc."
                  disabled={editingSource?.isDefault}
                />
                <p className="text-xs text-gray-500">Internal identifier (no spaces)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="displayName">
                  Display Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="displayName"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="InvoiceBug Application"
                />
                <p className="text-xs text-gray-500">User-friendly name</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="color">Brand Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#3B82F6"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pipelineStatus">Default Pipeline Status</Label>
                <Select
                  value={formData.defaultPipelineStatus || 'lead'}
                  onValueChange={(value: PipelineStatus) =>
                    setFormData({ ...formData, defaultPipelineStatus: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PIPELINE_STATUSES.map((status) => (
                      <SelectItem key={status} value={status} className="capitalize">
                        {status.replace('-', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bankingPartner">Banking Partner (Optional)</Label>
              <Select
                value={formData.bankingPartnerId || '__none__'}
                onValueChange={(value) =>
                  setFormData({ ...formData, bankingPartnerId: value === '__none__' ? undefined : value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select banking partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {bankingPartners
                    .filter((partner) => partner.isActive)
                    .map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Banking partner to use for applications from this source
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this application source..."
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="isActive">Active Status</Label>
                <p className="text-sm text-gray-500">
                  {formData.isActive ? 'Source is active and available' : 'Source is inactive'}
                </p>
              </div>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingSource ? 'Update Source' : 'Create Source'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application Source</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingSource?.displayName}</strong>?
              This action cannot be undone and will affect merchant application tracking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingSource(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
            >
              {saving ? 'Deleting...' : 'Delete Source'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
